import { useState, type FormEvent } from 'react'
import { ChevronRight, Compass, Gamepad2, GraduationCap, PartyPopper, Users, PackageOpen } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { parseInviteCode, type GuildTemplate } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { ChannelType } from '@/types/api'
import { cx } from '@/lib/cx'
import { Modal } from '../settings/Modal'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import styles from './AddGuildModal.module.css'

const templates: { id: GuildTemplate; label: string; hint: string; icon: React.ReactNode }[] = [
  { id: 'default', label: 'Свой шаблон', hint: 'Общий чат и один голосовой канал', icon: <Compass size={22} /> },
  { id: 'gaming', label: 'Игровой', hint: 'Правила, новости, поиск группы, рейды', icon: <Gamepad2 size={22} /> },
  { id: 'friends', label: 'Друзья', hint: 'Общий, игры, музыка, лаундж', icon: <PartyPopper size={22} /> },
  { id: 'study', label: 'Учёба', hint: 'Объявления, вопросы, лекции', icon: <GraduationCap size={22} /> },
  { id: 'community', label: 'Сообщество', hint: 'Правила, знакомства, мемы, идеи', icon: <Users size={22} /> },
  { id: 'empty', label: 'Пустой', hint: 'Без каналов — для импорта структуры из Discord', icon: <PackageOpen size={22} /> },
]

/** Добавить сервер: создать свой из шаблона или войти по приглашению. */
export function AddGuildModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<'choose' | 'create' | 'join'>('choose')
  const [template, setTemplate] = useState<GuildTemplate>('default')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const createGuild = useGuildStore((s) => s.createGuild)
  const joinByCode = useGuildStore((s) => s.joinByCode)
  const openGuild = useAppStore((s) => s.openGuild)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      const d = describeError(err)
      setError(d.fields.name ?? d.fields.template ?? d.message)
    } finally {
      setBusy(false)
    }
  }

  const create = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      const id = await createGuild(name.trim(), template)
      const first = Object.values(useGuildStore.getState().channels[id] ?? {}).find((c) => c.type === ChannelType.GuildText)
      openGuild(id, first?.id ?? null)
      onClose()
    })
  }

  const join = (e: FormEvent) => {
    e.preventDefault()
    const parsed = parseInviteCode(code)
    if (!parsed) {
      setError('Введите ссылку вида klichat.ru/invite/код или сам код.')
      return
    }
    void run(async () => {
      const inv = await joinByCode(parsed)
      openGuild(inv.guild.id, inv.channel.id)
      onClose()
    })
  }

  if (step === 'create') {
    return (
      <Modal title="Настройте свой сервер" onClose={onClose} width={480}>
        <form className={auth.form} onSubmit={create} noValidate>
          <p className={styles.text}>Дайте серверу название и выберите набор каналов. Всё это можно поменять потом.</p>
          <div className={styles.templates} role="radiogroup" aria-label="Шаблон">
            {templates.map((t) => (
              <button key={t.id} type="button" role="radio" aria-checked={template === t.id} className={cx(styles.template, template === t.id && styles.templateActive)} onClick={() => setTemplate(t.id)}>
                <span className={styles.templateIcon}>{t.icon}</span>
                <span className={styles.templateText}>
                  <span className={styles.templateLabel}>{t.label}</span>
                  <span className={styles.templateHint}>{t.hint}</span>
                </span>
              </button>
            ))}
          </div>
          <Field label="Название сервера" htmlFor="guild-name" error={error ?? undefined}>
            <input id="guild-name" className={auth.input} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="Например, Клан «Вечерние»" autoFocus required />
          </Field>
          <div className={styles.actions}>
            <button type="button" className={styles.back} onClick={() => setStep('choose')}>Назад</button>
            <button type="submit" className={auth.primary} disabled={busy || name.trim().length < 2}>{busy ? 'Создаём…' : 'Создать'}</button>
          </div>
        </form>
      </Modal>
    )
  }

  if (step === 'join') {
    return (
      <Modal title="Присоединиться к серверу" onClose={onClose} width={480}>
        <form className={auth.form} onSubmit={join} noValidate>
          <p className={styles.text}>Вставьте приглашение, чтобы войти на существующий сервер.</p>
          <Field label="Ссылка-приглашение" htmlFor="invite-code" hint="Например, klichat.ru/invite/Kx7pQ2mN" error={error ?? undefined}>
            <input id="invite-code" className={auth.input} value={code} onChange={(e) => setCode(e.target.value)} placeholder="https://klichat.ru/invite/…" autoFocus required spellCheck={false} />
          </Field>
          <div className={styles.actions}>
            <button type="button" className={styles.back} onClick={() => setStep('choose')}>Назад</button>
            <button type="submit" className={auth.primary} disabled={busy || !code.trim()}>{busy ? 'Входим…' : 'Присоединиться'}</button>
          </div>
        </form>
      </Modal>
    )
  }

  return (
    <Modal title="Создать сервер" onClose={onClose} width={480}>
      <p className={styles.text}>Сервер — это место для вас и ваших друзей. Создайте свой или присоединитесь к существующему.</p>
      <button type="button" className={styles.option} onClick={() => setStep('create')}>
        <span className={styles.optionIcon}><Compass size={26} /></span>
        <span className={styles.optionLabel}>Создать свой</span>
        <ChevronRight size={20} className={styles.chevron} />
      </button>
      <div className={styles.joinBox}>
        <span className={styles.joinTitle}>Уже есть приглашение?</span>
        <button type="button" className={styles.joinButton} onClick={() => setStep('join')}>Присоединиться к серверу</button>
      </div>
    </Modal>
  )
}
